import React, { useState, useEffect } from 'react';
import { Phone, ArrowLeft, Home, Scale, FileText, Download, Search, CheckCircle, Globe, AlertCircle, Users, DollarSign, Gavel, Shield, Building, CreditCard } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { getDynamicPhoneNumber } from '../../services/constants';

const TermsAndConditionsPage = () => {
  const [fallbackPhone, setFallbackPhone] = useState('+19162263934');
  const [language, setLanguage] = useState('en');
  const [expandedSection, setExpandedSection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [acceptanceTracked, setAcceptanceTracked] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Set up dynamic fallback phone number
  useEffect(() => {
    const updateFallbackPhone = async () => {
      try {
        const dynamicPhone = await getDynamicPhoneNumber();
        setFallbackPhone(dynamicPhone);
      } catch (error) {
        setFallbackPhone('+19162263934');
      }
    };

    updateFallbackPhone();

    const handleCurrentAgentChange = () => {
      updateFallbackPhone();
    };

    window.addEventListener('currentAgentChanged', handleCurrentAgentChange);

    return () => {
      window.removeEventListener('currentAgentChanged', handleCurrentAgentChange);
    };
  }, []);

  const formatPhoneDisplay = (phone) => {
    return phone?.replace('+1', '')?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  const goBack = () => {
    if (window.history?.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const goHome = () => {
    navigate('/');
  };

  const canGoBack = location?.pathname !== '/';

  const termsContent = {
    en: {
      title: 'Terms and Conditions - Sailors Bail Bonds',
      lastUpdated: 'Last updated: January 30, 2025',
      introduction: 'These Terms and Conditions establish the legal agreement between Sailors Bail Bonds and our clients for all bail bond services. By engaging our services, you agree to these terms, payment obligations, and liability limitations specific to bail bond transactions in California.',
      sections: [
        {
          title: 'Service Scope and Bail Bond Types',
          icon: 'Scale',
          content: [
            'Cash bonds: Full bail amount paid directly to the court with 100% refund upon case completion',
            'Surety bonds: 10% premium fee (non-refundable) plus collateral as required by California law',
            'Property bonds: Real estate or valuable assets used as collateral for bail amount',
            'Immigration bonds: Specialized bonds for federal immigration detention cases',
            'Appeal bonds: Bonds for defendants appealing their conviction or sentence',
            'Transfer bonds: Bonds transferred from other jurisdictions or bail bond companies',
            'Violation of probation bonds: Bonds for defendants arrested for probation violations',
            'Out-of-county bonds: Services for arrests in counties outside Sacramento'
          ]
        },
        {
          title: 'Client Responsibilities and Obligations',
          icon: 'Users',
          content: [
            'Appear at all scheduled court dates and legal proceedings as required',
            'Maintain current contact information and notify us of any address changes',
            'Comply with all bail conditions set by the court or law enforcement',
            'Report any arrests, citations, or legal issues while on bail',
            'Provide accurate and truthful information during the application process',
            'Allow reasonable contact and check-ins as required for case monitoring',
            'Surrender immediately if requested by Sailors Bail Bonds or law enforcement',
            'Notify us within 24 hours of any travel plans outside California'
          ]
        },
        {
          title: 'Payment Terms and Fee Structure',
          icon: 'DollarSign',
          content: [
            'Premium fee: 10% of total bail amount (California state maximum, non-refundable)',
            'Payment plans: Available with approved credit and co-signer requirements',
            'Down payment: Minimum 10% of premium due at bond signing (may be waived OAC)',
            'Collateral requirements: May include real estate, vehicles, jewelry, or other valuable assets',
            'Late payment fees: $25 per month for payments more than 10 days past due',
            'Returned payment fees: $35 for checks returned due to insufficient funds',
            'Collection costs: Client responsible for all reasonable collection expenses',
            'Emergency service fees: Additional $100 for bonds written outside normal business hours'
          ]
        },
        {
          title: 'Collateral Requirements and Asset Protection',
          icon: 'Shield',
          content: [
            'Collateral value must equal or exceed total bail amount plus costs',
            'Real estate: Property deeds, mortgage information, and current appraisals required',
            'Vehicles: Clear titles, registration, and insurance documentation required',
            'Personal property: Detailed inventory and appraisals for valuable items',
            'Financial assets: Bank statements, investment accounts, and retirement funds',
            'Collateral release: Assets returned within 30 days of case completion and premium payment',
            'Asset protection: All collateral is insured and securely stored during bond period',
            'Substitute collateral: May be exchanged with prior written approval'
          ]
        },
        {
          title: 'Cancellation Policies and Bond Forfeiture',
          icon: 'AlertCircle',
          content: [
            'Bond cancellation: Available only with court approval and full premium payment',
            'Forfeiture process: Initiated when defendant fails to appear in court as scheduled',
            'Grace period: 180 days from forfeiture date to locate and surrender defendant',
            'Recovery costs: Client responsible for all costs associated with defendant apprehension',
            'Collateral liquidation: Assets may be sold to cover forfeiture amount and costs',
            'Reinstatement: Possible with court approval, additional fees, and increased collateral',
            'Partial refunds: No partial refunds of premium fees under any circumstances',
            'Co-signer liability: All co-signers remain liable for full bail amount and costs'
          ]
        },
        {
          title: 'Co-signer and Indemnitor Obligations',
          icon: 'FileText',
          content: [
            'Financial liability for full bail amount plus all associated costs and fees',
            'Responsibility to ensure defendant appears for all court dates',
            'Authority to surrender defendant to custody at any time before court appearance',
            'Obligation to provide accurate financial and personal information',
            'Duty to report defendant location and contact information changes',
            'Liability for recovery costs if defendant fails to appear',
            'Agreement to pay all collection costs, attorney fees, and court costs',
            'Understanding that indemnity agreement remains valid until case completion'
          ]
        },
        {
          title: 'California State Regulatory Compliance',
          icon: 'Building',
          content: [
            'Licensed by California Department of Insurance (License #1BG43697)',
            'Compliance with California Insurance Code Section 1800-1823',
            'Adherence to Penal Code Section 1269b regarding bail bond procedures',
            'Following court rules and local jail policies for all jurisdictions served',
            'Proper licensing and insurance for all bail enforcement activities',
            'Compliance with fair debt collection practices under state and federal law',
            'Adherence to privacy laws including California Consumer Privacy Act (CCPA)',
            'Regular reporting to regulatory agencies as required by law'
          ]
        },
        {
          title: 'Dispute Resolution and Legal Procedures',
          icon: 'Gavel',
          content: [
            'Mediation: All disputes must first attempt resolution through mediation',
            'Arbitration: Binding arbitration required for disputes exceeding $5,000',
            'Jurisdiction: All legal actions must be filed in Sacramento County Superior Court',
            'Attorney fees: Prevailing party entitled to reasonable attorney fees and costs',
            'Time limitations: All claims must be filed within one year of the dispute',
            'Class action waiver: Clients waive right to participate in class action lawsuits',
            'Severability: Invalid provisions do not invalidate the entire agreement',
            'Governing law: Agreement governed by California state law and regulations'
          ]
        },
        {
          title: 'Emergency Contact and Crisis Response',
          icon: 'Phone',
          content: [
            '24/7 emergency hotline: (916) 226-3934 for urgent matters',
            'Text messaging: Available for non-emergency updates and communication',
            'Emergency surrender: Procedures for immediate defendant surrender when required',
            'Court notification: Immediate notification of court date changes or emergencies',
            'Medical emergencies: Protocols for defendant medical emergencies while on bail',
            'Legal emergencies: Immediate response for arrest warrants or legal complications',
            'Family notifications: Emergency contact procedures for family members',
            'Crisis intervention: Available resources for defendants in crisis situations'
          ]
        }
      ],
      digitalAgreement: {
        title: 'Digital Agreement Acceptance',
        description: 'By clicking "I Accept These Terms," you acknowledge that you have read, understood, and agree to be legally bound by these Terms and Conditions.',
        acceptButton: 'I Accept These Terms',
        declineButton: 'I Do Not Accept',
        trackingNotice: 'Your acceptance will be digitally recorded with timestamp and IP address for legal purposes.'
      }
    },
    es: {
      title: 'Términos y Condiciones - Sailors Bail Bonds',
      lastUpdated: 'Última actualización: 30 de enero de 2025',
      introduction: 'Estos Términos y Condiciones establecen el acuerdo legal entre Sailors Bail Bonds y nuestros clientes para todos los servicios de fianza. Al contratar nuestros servicios, usted acepta estos términos, obligaciones de pago y limitaciones de responsabilidad específicas para transacciones de fianza en California.',
      sections: [
        {
          title: 'Alcance del Servicio y Tipos de Fianza',
          icon: 'Scale',
          content: [
            'Fianzas en efectivo: Monto total de fianza pagado directamente al tribunal con 100% de reembolso al completar el caso',
            'Fianzas de garantía: Tarifa de prima del 10% (no reembolsable) más garantía según lo requiere la ley de California',
            'Fianzas de propiedad: Bienes raíces o activos valiosos utilizados como garantía por el monto de la fianza',
            'Fianzas de inmigración: Fianzas especializadas para casos de detención de inmigración federal',
            'Fianzas de apelación: Fianzas para acusados que apelan su condena o sentencia',
            'Fianzas de transferencia: Fianzas transferidas de otras jurisdicciones o compañías de fianza',
            'Fianzas por violación de libertad condicional: Fianzas para acusados arrestados por violaciones de libertad condicional',
            'Fianzas fuera del condado: Servicios para arrestos en condados fuera de Sacramento'
          ]
        },
        {
          title: 'Responsabilidades y Obligaciones del Cliente',
          icon: 'Users',
          content: [
            'Aparecer en todas las fechas de corte programadas y procedimientos legales según se requiera',
            'Mantener información de contacto actual y notificarnos de cualquier cambio de dirección',
            'Cumplir con todas las condiciones de fianza establecidas por el tribunal o las fuerzas del orden',
            'Reportar cualquier arresto, citación o problema legal mientras esté en fianza',
            'Proporcionar información precisa y veraz durante el proceso de solicitud',
            'Permitir contacto razonable y verificaciones según se requiera para monitoreo de caso',
            'Rendirse inmediatamente si lo solicita Sailors Bail Bonds o las fuerzas del orden',
            'Notificarnos dentro de 24 horas de cualquier plan de viaje fuera de California'
          ]
        },
        {
          title: 'Términos de Pago y Estructura de Tarifas',
          icon: 'DollarSign',
          content: [
            'Tarifa de prima: 10% del monto total de fianza (máximo del estado de California, no reembolsable)',
            'Planes de pago: Disponibles con crédito aprobado y requisitos de co-firmante',
            'Pago inicial: Mínimo 10% de prima debido al firmar la fianza (puede ser exonerado OAC)',
            'Requisitos de garantía: Pueden incluir bienes raíces, vehículos, joyas u otros activos valiosos',
            'Tarifas por pago tardío: $25 por mes para pagos con más de 10 días de retraso',
            'Tarifas por pago devuelto: $35 por cheques devueltos por fondos insuficientes',
            'Costos de cobranza: Cliente responsable de todos los gastos razonables de cobranza',
            'Tarifas de servicio de emergencia: $100 adicionales por fianzas escritas fuera del horario comercial normal'
          ]
        },
        {
          title: 'Requisitos de Garantía y Protección de Activos',
          icon: 'Shield',
          content: [
            'El valor de la garantía debe igualar o exceder el monto total de fianza más costos',
            'Bienes raíces: Escrituras de propiedad, información de hipoteca y avalúos actuales requeridos',
            'Vehículos: Títulos limpios, registro y documentación de seguro requeridos',
            'Propiedad personal: Inventario detallado y avalúos para artículos valiosos',
            'Activos financieros: Estados de cuenta bancarios, cuentas de inversión y fondos de jubilación',
            'Liberación de garantía: Activos devueltos dentro de 30 días de completar el caso y pago de prima',
            'Protección de activos: Toda garantía está asegurada y almacenada de forma segura durante el período de fianza',
            'Garantía sustituta: Puede ser intercambiada con aprobación previa por escrito'
          ]
        },
        {
          title: 'Políticas de Cancelación y Decomiso de Fianza',
          icon: 'AlertCircle',
          content: [
            'Cancelación de fianza: Disponible solo con aprobación del tribunal y pago completo de prima',
            'Proceso de decomiso: Iniciado cuando el acusado no se presenta al tribunal según lo programado',
            'Período de gracia: 180 días desde la fecha de decomiso para localizar y entregar al acusado',
            'Costos de recuperación: Cliente responsable de todos los costos asociados con la aprehensión del acusado',
            'Liquidación de garantía: Los activos pueden ser vendidos para cubrir el monto de decomiso y costos',
            'Reintegración: Posible con aprobación del tribunal, tarifas adicionales y garantía aumentada',
            'Reembolsos parciales: No hay reembolsos parciales de tarifas de prima bajo ninguna circunstancia',
            'Responsabilidad del co-firmante: Todos los co-firmantes permanecen responsables del monto total de fianza y costos'
          ]
        },
        {
          title: 'Obligaciones del Co-firmante e Indemnizador',
          icon: 'FileText',
          content: [
            'Responsabilidad financiera por el monto total de fianza más todos los costos y tarifas asociados',
            'Responsabilidad de asegurar que el acusado aparezca en todas las fechas de corte',
            'Autoridad para entregar al acusado a custodia en cualquier momento antes de la comparecencia ante el tribunal',
            'Obligación de proporcionar información financiera y personal precisa',
            'Deber de reportar cambios en la ubicación y información de contacto del acusado',
            'Responsabilidad por costos de recuperación si el acusado no se presenta',
            'Acuerdo de pagar todos los costos de cobranza, honorarios de abogado y costos judiciales',
            'Entendimiento de que el acuerdo de indemnización permanece válido hasta la finalización del caso'
          ]
        },
        {
          title: 'Cumplimiento Regulatorio del Estado de California',
          icon: 'Building',
          content: [
            'Licenciado por el Departamento de Seguros de California (Licencia #1BG43697)',
            'Cumplimiento con el Código de Seguros de California Sección 1800-1823',
            'Adherencia al Código Penal Sección 1269b respecto a procedimientos de fianza',
            'Siguiendo reglas judiciales y políticas de cárceles locales para todas las jurisdicciones servidas',
            'Licenciamiento apropiado y seguro para todas las actividades de cumplimiento de fianza',
            'Cumplimiento con prácticas de cobranza de deudas justas bajo la ley estatal y federal',
            'Adherencia a leyes de privacidad incluyendo la Ley de Privacidad del Consumidor de California (CCPA)',
            'Reporte regular a agencias regulatorias según lo requiere la ley'
          ]
        },
        {
          title: 'Resolución de Disputas y Procedimientos Legales',
          icon: 'Gavel',
          content: [
            'Mediación: Todas las disputas deben primero intentar resolución a través de mediación',
            'Arbitraje: Arbitraje vinculante requerido para disputas que excedan $5,000',
            'Jurisdicción: Todas las acciones legales deben ser presentadas en el Tribunal Superior del Condado de Sacramento',
            'Honorarios de abogado: La parte ganadora tiene derecho a honorarios de abogado razonables y costos',
            'Limitaciones de tiempo: Todas las reclamaciones deben ser presentadas dentro de un año de la disputa',
            'Renuncia a acción de clase: Los clientes renuncian al derecho de participar en demandas de acción de clase',
            'Separabilidad: Las disposiciones inválidas no invalidan todo el acuerdo',
            'Ley aplicable: Acuerdo regido por la ley estatal de California y regulaciones'
          ]
        },
        {
          title: 'Contacto de Emergencia y Respuesta a Crisis',
          icon: 'Phone',
          content: [
            'Línea directa de emergencia 24/7: (916) 226-3934 para asuntos urgentes',
            'Mensajes de texto: Disponibles para actualizaciones y comunicación no urgente',
            'Entrega de emergencia: Procedimientos para entrega inmediata del acusado cuando se requiera',
            'Notificación judicial: Notificación inmediata de cambios en fechas de corte o emergencias',
            'Emergencias médicas: Protocolos para emergencias médicas del acusado mientras esté en fianza',
            'Emergencias legales: Respuesta inmediata para órdenes de arresto o complicaciones legales',
            'Notificaciones familiares: Procedimientos de contacto de emergencia para miembros de la familia',
            'Intervención en crisis: Recursos disponibles para acusados en situaciones de crisis'
          ]
        }
      ],
      digitalAgreement: {
        title: 'Aceptación de Acuerdo Digital',
        description: 'Al hacer clic en "Acepto Estos Términos," usted reconoce que ha leído, entendido y acepta estar legalmente obligado por estos Términos y Condiciones.',
        acceptButton: 'Acepto Estos Términos',
        declineButton: 'No Acepto',
        trackingNotice: 'Su aceptación será registrada digitalmente con marca de tiempo y dirección IP para propósitos legales.'
      }
    }
  };

  const currentContent = termsContent?.[language] || termsContent?.en;

  const filteredSections = currentContent?.sections?.filter(section =>
    section?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    section?.content?.some(item => item?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
  );

  const getIcon = (iconName) => {
    const icons = {
      Scale,
      Users,
      DollarSign,
      Shield,
      AlertCircle,
      FileText,
      Building,
      Gavel,
      Phone,
      CreditCard
    };
    const IconComponent = icons?.[iconName] || FileText;
    return <IconComponent className="w-6 h-6" aria-hidden="true" />;
  };

  const handleAcceptance = (accepted) => {
    if (accepted) {
      setAcceptanceTracked(true);
      // Track acceptance with timestamp
      const acceptanceData = {
        timestamp: new Date()?.toISOString(),
        userAgent: navigator?.userAgent,
        accepted: true,
        language: language
      };
      localStorage.setItem('sailorsBailTermsAcceptance', JSON.stringify(acceptanceData));
      alert(language === 'es' ?'Términos aceptados exitosamente. Su aceptación ha sido registrada.' :'Terms accepted successfully. Your acceptance has been recorded.');
    } else {
      alert(language === 'es' ?'Debe aceptar los términos para proceder con nuestros servicios.' :'You must accept the terms to proceed with our services.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Terms and Conditions | Sailors Bail Bonds | Legal Agreement & Client Obligations</title>
        <meta name="description" content="Comprehensive terms and conditions for Sailors Bail Bonds covering service agreements, payment terms, client responsibilities, and California bail bond regulations." />
        <meta name="keywords" content="bail bond terms, legal agreement, client obligations, payment terms, California bail bonds, co-signer responsibilities" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Navigation Header */}
        <section className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white py-4 px-4 shadow-lg sticky top-0 z-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {canGoBack && (
                  <button
                    onClick={goBack}
                    className="flex items-center bg-white/20 text-white px-3 py-2 rounded-full text-sm hover:bg-white/30 transition-colors"
                    aria-label="Go back"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" aria-hidden="true" />
                    {language === 'es' ? 'Regresar' : 'Go Back'}
                  </button>
                )}
                
                <button
                  onClick={goHome}
                  className="flex items-center bg-white/20 text-white px-3 py-2 rounded-full text-sm hover:bg-white/30 transition-colors"
                  aria-label="Go home"
                >
                  <Home className="w-4 h-4 mr-1" aria-hidden="true" />
                  {language === 'es' ? 'Inicio' : 'Home'}
                </button>

                <a 
                  href={`tel:${fallbackPhone}`}
                  className="flex items-center bg-white text-red-600 px-4 py-2 rounded-full font-bold hover:bg-red-50 transition-colors text-sm"
                >
                  <Phone className="w-4 h-4 mr-2" aria-hidden="true" />
                  {language === 'es' ? 'Llamar' : 'Call'} {formatPhoneDisplay(fallbackPhone)}
                </a>
              </div>

              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
                  className="bg-white/20 text-white px-3 py-2 rounded-full text-sm hover:bg-white/30 transition-colors flex items-center"
                >
                  <Globe className="w-4 h-4 mr-1" aria-hidden="true" />
                  {language === 'es' ? 'English' : 'Español'}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Header Section */}
        <section className="bg-white shadow-sm py-12">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-6">
                <div className="bg-red-600 text-white p-4 rounded-full mr-4">
                  <Scale className="w-12 h-12" aria-hidden="true" />
                </div>
                <div className="text-left">
                  <h1 className="text-4xl lg:text-5xl font-black text-gray-900 mb-2">
                    {currentContent?.title}
                  </h1>
                  <p className="text-lg text-gray-600">
                    {currentContent?.lastUpdated}
                  </p>
                </div>
              </div>
              
              <div className="max-w-4xl mx-auto">
                <p className="text-xl text-gray-700 leading-relaxed">
                  {currentContent?.introduction}
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <button
                onClick={() => window.print()}
                className="flex items-center bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-bold transition-colors"
              >
                <Download className="w-5 h-5 mr-2" aria-hidden="true" />
                {language === 'es' ? 'Descargar PDF' : 'Download PDF'}
              </button>
              
              <div className="relative">
                <input
                  type="text"
                  placeholder={language === 'es' ? 'Buscar términos...' : 'Search terms...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-red-500 focus:ring-0 w-full sm:w-80"
                />
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" aria-hidden="true" />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="space-y-8">
              {filteredSections?.map((section, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="bg-red-600 text-white p-3 rounded-full mr-4">
                        {getIcon(section?.icon)}
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {section?.title}
                      </h2>
                    </div>
                    <div className="flex-shrink-0">
                      {expandedSection === index ? (
                        <CheckCircle className="w-6 h-6 text-red-600" aria-hidden="true" />
                      ) : (
                        <FileText className="w-6 h-6 text-gray-400" aria-hidden="true" />
                      )}
                    </div>
                  </button>

                  {expandedSection === index && (
                    <div className="px-6 pb-6">
                      <div className="border-t border-gray-200 pt-6">
                        <ul className="space-y-4">
                          {section?.content?.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start">
                              <Gavel className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-1" aria-hidden="true" />
                              <p className="text-gray-700 leading-relaxed">
                                {item}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Digital Agreement Section */}
        <section className="py-16 bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
              <div className="text-center mb-8">
                <div className="bg-blue-600 text-white p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <FileText className="w-8 h-8" aria-hidden="true" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-4">
                  {currentContent?.digitalAgreement?.title}
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  {currentContent?.digitalAgreement?.description}
                </p>
                <p className="text-sm text-gray-600 italic">
                  {currentContent?.digitalAgreement?.trackingNotice}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => handleAcceptance(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg flex items-center justify-center"
                >
                  <CheckCircle className="w-6 h-6 mr-3" aria-hidden="true" />
                  {currentContent?.digitalAgreement?.acceptButton}
                </button>
                
                <button
                  onClick={() => handleAcceptance(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center"
                >
                  <AlertCircle className="w-6 h-6 mr-3" aria-hidden="true" />
                  {currentContent?.digitalAgreement?.declineButton}
                </button>
              </div>

              {acceptanceTracked && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mr-3" aria-hidden="true" />
                    <p className="text-green-800 font-semibold">
                      {language === 'es' ?'Términos aceptados exitosamente - Registro completo' :'Terms accepted successfully - Record complete'
                      }
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Important Legal Notice */}
        <section className="py-12 bg-red-50 border-t border-red-200">
          <div className="max-w-6xl mx-auto px-6">
            <div className="bg-white rounded-2xl p-8 border-l-4 border-red-500 shadow-lg">
              <div className="flex items-start">
                <Gavel className="w-8 h-8 text-red-600 mr-4 flex-shrink-0 mt-1" aria-hidden="true" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {language === 'es' ? 'Aviso Legal Importante' : 'Important Legal Notice'}
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {language === 'es' ?'Estos términos constituyen un acuerdo legal vinculante. Al utilizar nuestros servicios, usted acepta todas las obligaciones financieras y legales descritas. El incumplimiento puede resultar en acciones legales y pérdida de garantía.' :'These terms constitute a binding legal agreement. By using our services, you accept all financial and legal obligations outlined. Failure to comply may result in legal action and forfeiture of collateral.'
                    }
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    {language === 'es' ?'Para preguntas sobre términos legales o obligaciones específicas, consulte con nuestros agentes licenciados disponibles 24/7 al (916) 226-3934.' :'For questions about legal terms or specific obligations, consult with our licensed agents available 24/7 at (916) 226-3934.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer Call-to-Action */}
        <section className="py-16 bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-3xl lg:text-4xl font-black mb-6">
              {language === 'es' ? '¿Listo Para Proceder?' : 'Ready to Proceed?'}
            </h2>
            <p className="text-xl mb-8 text-red-100">
              {language === 'es' ?'Nuestros agentes licenciados están listos para ayudarle con su caso de fianza' :'Our licensed agents are ready to assist with your bail bond case'
              }
            </p>
            
            <div className="flex flex-col lg:flex-row gap-6 justify-center items-center">
              <a 
                href={`tel:${fallbackPhone}`}
                className="bg-white text-red-600 px-8 py-4 rounded-2xl font-black text-xl hover:bg-red-50 transition-all transform hover:scale-105 shadow-2xl flex items-center"
              >
                <Phone className="w-6 h-6 mr-3" aria-hidden="true" />
                {formatPhoneDisplay(fallbackPhone)}
              </a>
              
              <button
                onClick={() => navigate('/')}
                className="bg-red-700 hover:bg-red-800 text-white px-6 py-4 rounded-2xl font-bold text-lg transition-all transform hover:scale-105 shadow-2xl border-2 border-red-500 flex items-center"
              >
                <FileText className="w-5 h-5 mr-2" aria-hidden="true" />
                {language === 'es' ? 'Comenzar Proceso de Fianza' : 'Start Bail Process'}
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TermsAndConditionsPage;